import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { withRouter } from 'react-router-dom';

import classNames from 'classnames';

import { Image, Label, Menu, Popup } from 'semantic-ui-react';

import iconHelp from '../img/icon-help.svg';
import '../css/TopNav.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import Auth from '../managers/AuthManager';

import { VIEW_SELECTION } from '../managers/NavigationManager';

import { TOP_NAV_NAME } from '../constants';

import GuidedTourService from '../services/GuidedTourService';
import InitService from '../services/InitService';
import PopupService from '../services/PopupService';
import ProfileMenuService from '../services/ProfileMenuService';
import TeacherProductViewService from '../services/TeacherProductViewService';
import TopNavService from '../services/TopNavService';
import UserService from '../services/UserService';

export default
@inject(
  'appManager',
  'contentManager',
  'navigationManager',
  'notificationManager',
  'productManager',
  'schoolManager',
  'teacherProductViewManager',
  'topNavManager',
  'userManager')
@observer
class TopNav extends Component {
  constructor(props) {
    super(props);
    this.AriaSkipLink = SatCoreComponent('AriaSkipLink');
    this.EnvelopeIcon = SatCoreComponent('EnvelopeIcon');
    this.LogoButtons = SatCoreComponent('LogoButtons');
    this.ProfileMenu = SatCoreComponent('ProfileMenu');

    this.menuBarRef = React.createRef();
  }

  componentDidMount() {
    const { appManager, history, topNavManager } = this.props;

    topNavManager.setActiveTopNavName(TOP_NAV_NAME.TopNav);

    TeacherProductViewService.updateTeacherProductNavStatus(window.location.pathname);

    InitService.initActiveUserRoleAsHtmlBodyAttr();

    // send user to a specified custom 'main' nav tab if the following conditions are true:
    // (1) user has the proper satellite and publisher permissions to see the given tab (i.e. the tab is in the displayed list), and
    // (2) user is on currently on the dashboard tab (i.e. likely via redirection after login), and
    // (3) user has likely not manually clicked on the 'dashboard' nav tab since login (i.e. ?view='dashboard' param is absent), and
    // (4) satelliteLayoutConfig has a 'mainTopNavId' that is **not** the dashboard ('default')
    const { topNavObj } = TopNavService.getTeacherMainTopNavInfo() || {};
    const mainTopNavId = topNavObj?._id;

    if (topNavObj?.functionKey) {
      const pathname = window?.location?.pathname;
      const urlParams = new URLSearchParams(window.location.search);
      if (mainTopNavId !== 'default' && pathname === '/' && urlParams?.get?.('view') !== VIEW_SELECTION.DASHBOARD) {
        this[topNavObj?.functionKey]?.();
      }
    }

    this.unlisten = history.listen((location, _action) => {
      appManager.setLocation(location);
      TeacherProductViewService.updateTeacherProductNavStatus(location.pathname);
    });
  }

  componentWillUnmount() {
    InitService.removeHtmlBodyRoleAttr();
    this.unlisten?.();
  }

  showProfile = () => {
    const { history } = this.props;
    history.push(`/profile?view=${VIEW_SELECTION.PROFILE}`, { url: history.createHref(history.location) });
  };

  showArchivedClasses = () => {
    const { history, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.ARCHIVED_CLASSES);
    history.push(`/archived-classes?view=${VIEW_SELECTION.ARCHIVED_CLASSES}`);
  };

  handleClickGuidedTour = () => {
    const { topNavManager } = this.props;
    topNavManager.setIsTopNavProfileMenuOpen(false);
    // Small delay to allow menu to fully close and clear active states before launching tour
    setTimeout(() => {
      GuidedTourService.launchGuidedTour();
    }, 300);
  };

  handleClickRoleSwitcher = () => {
    const { history } = this.props;
    GuidedTourService.removeGuidedTourLauncherIfApplicable();
    UserService.showRoleSwitcher(history);
  };

  showExternalDashboard = async () => {
    const { history } = this.props;
    await history.push(`/externalDashboard?view=${VIEW_SELECTION.EXTERNAL_DASHBOARD}`);
  }

  showClasses = async () => {
    const { history, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.DASHBOARD);
    await history.push(`/?view=${VIEW_SELECTION.DASHBOARD}`);
  };

  showContent = async () => {
    const { history, navigationManager, userManager } = this.props;
    const { allowTeacherLibraryContentView } = userManager;
    if (allowTeacherLibraryContentView) {
      navigationManager.setView(VIEW_SELECTION.CONTENT);
      await history.push(`/content?view=${VIEW_SELECTION.CONTENT}`);
    }
  }

  showProducts = async () => {
    const { history, navigationManager, teacherProductViewManager } = this.props;
    if (teacherProductViewManager.allowTeacherProductView) {
      navigationManager.setView(VIEW_SELECTION.PRODUCTS);
      await history.push(`/products?view=${VIEW_SELECTION.PRODUCTS}`);
    }
  }

  showAssignments = async () => {
    const { history, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.ASSIGNMENTS);
    await history.push(`/assignments?view=${VIEW_SELECTION.ASSIGNMENTS}`);
  };

  handleClickTestBuilder = async () => {
    const {
      history,
      navigationManager, schoolManager, userManager
    } = this.props;
    if (userManager.canViewSatelliteTestBuilder) {
      navigationManager.setView(VIEW_SELECTION.TEST_BUILDER);
      navigationManager.clearAllPaths();
      schoolManager.setSearchText('');
      await history.push(`/testbuilder?view=${VIEW_SELECTION.TEST_BUILDER}`);
    }
  }

  showReports = async () => {
    const { history, navigationManager } = this.props;
    const noAddDefaultBreadcrumb = true;
    navigationManager.clearAllPaths(noAddDefaultBreadcrumb);
    navigationManager.setView(VIEW_SELECTION.REPORTS);
    await history.push(`/reports?view=${VIEW_SELECTION.REPORTS}`);
  }

  showHelpWindow = async () => {
    const { contentManager } = this.props;
    if (contentManager.helpWindowUrl !== null) {
      window.open(contentManager.helpWindowUrl, '_blank');
    }
  }

  showNotifications = () => {
    const { history, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.NOTIFICATIONS);
    history.push(`/notifications?view=${VIEW_SELECTION.NOTIFICATIONS}`);
  }

  showResources = async () => {
    const { history, navigationManager } = this.props;
    navigationManager.setView(VIEW_SELECTION.RESOURCES);
    await history.push(`/resources?view=${VIEW_SELECTION.RESOURCES}`);
  }

  /**
   * Per **DEMO-2504**, to better ensure the Notifications count in TopNav is current,
   *
   * we are now fetching notifications whenever the active TopNav section changes.
   */
  handleFetchNotifications = async () => {
    const { notificationManager, userManager } = this.props;
    await notificationManager.fetchManualNotifications({ page: 0, userId: userManager.userId });
    await notificationManager.fetchSystemNotifications({ page: 0, userId: userManager.userId });
  }

  handleLogout = async () => {
    const { history, navigationManager, t, topNavManager } = this.props;

    const strOfItemsToPreserve = '';

    const externalLogoutUrl = t('externalLogoutUrl');

    InitService.doLogoutCleanUp(strOfItemsToPreserve, { externalLogoutUrl });

    if (!externalLogoutUrl || !topNavManager.allowLoginRedirectUrl) {
      navigationManager.setView(VIEW_SELECTION.LOGIN);
      history.replace('/signin');
    }
  }

  isViewSelected = (view) => {
    const { navigationManager, productManager } = this.props;
    const { viewSelection } = navigationManager;

    if (productManager.FROM_TEACHER_PRODUCTS_NAV &&
      (viewSelection === VIEW_SELECTION.BOOK || viewSelection === VIEW_SELECTION.PRODUCTS)
    ) {
      return view === VIEW_SELECTION.PRODUCTS;
    } else {
      return viewSelection && viewSelection === view;
    }
  }

  isViewSelectedClass = (view) => (this.isViewSelected(view) ? 'selected' : null);

  renderMenuItem = (children, view, props, {
    _id,
    /* navTranslationKey = '', */
    navTranslationRolloverKey = '',
    urlPathname
  } = {}) => {
    const { productManager, t, topNavManager, userManager } = this.props;

    const { allowAuthKeyTopNavUrlParamAuthentication, treatTopNavTabsAsAnchorTags } = topNavManager;

    const rolloverMessage = t(navTranslationRolloverKey);

    let href;
    if (treatTopNavTabsAsAnchorTags && urlPathname) {
      href = `${urlPathname}?view=${view}`;
      if (allowAuthKeyTopNavUrlParamAuthentication) {
        href += `&authKey=${Auth.authKey}`;
        href += `&activePermissionId=${userManager.activePermissionId}`;
      }
    }

    return (!productManager.isFromProduct || productManager.FROM_TEACHER_PRODUCTS_NAV) && (
      PopupService.renderPopup({
        content: rolloverMessage,
        disabled: !rolloverMessage,
        key: _id,
        trigger: (
          <Menu.Item
            aria-selected={!!this.isViewSelectedClass(view)}
            as={href ? 'a' : 'li'}
            href={href}
            role='tab'
            tabIndex='0'
            {...props}
            className={['tnav-tab', this.isViewSelectedClass(view), (props || {}).className].filter((className) => className).join(' ')}
            onClick={async (event, _data) => {
              if (href) {
                event.preventDefault();
              }
              await this.handleFetchNotifications();
              if (productManager.shouldShowExpiredLicenseWarning) {
                productManager.setShouldShowExpiredLicenseWarning(false);
              }
              await props.onClick();
            }}>
            {children}
          </Menu.Item>
        ),
        wide: false
      })
    );
  }

  getNavTabs = () => {
    const { getNavTabs, productManager } = this.props;
    if (productManager.isFromProduct && !productManager.FROM_TEACHER_PRODUCTS_NAV) {
      return null;
    } else {
      return getNavTabs !== undefined ? (
        <>
          {getNavTabs(this.props, this)}
          {this.getAdditionalTopNavExternalResources()}
        </>
      ) : (
        <>
          {this.getBaseNavTabs()}
          {this.getAdditionalTopNavExternalResources()}
        </>
      );
    }
  };

  getBaseNavTabs = () => {
    const {
      t,
      teacherProductViewManager,
      userManager
    } = this.props;

    const { allowTeacherProductView } = teacherProductViewManager;
    const { allowTeacherLibraryContentView, canViewSatelliteTestBuilder } = userManager;
    const showTestBuilderTab = canViewSatelliteTestBuilder;

    const {
      USER_TOP_NAV,
      topNavOrderArray
    } = TopNavService.getMainTopNavInfo() || {};

    return topNavOrderArray?.map?.((tabNavId) => {
      const tabNavObj = USER_TOP_NAV[tabNavId];

      const { _id, className, functionKey, navTranslationKey, viewSelectionKey } = tabNavObj;

      switch (_id) {
        case USER_TOP_NAV.assignments._id:
          break;
        case USER_TOP_NAV.classrooms._id:
          break;
        case USER_TOP_NAV.externalDashboard._id:
          break;
        case USER_TOP_NAV.libraryContent._id:
          if (!allowTeacherLibraryContentView) {
            return;
          }
          break;
        case USER_TOP_NAV.reports._id:
          break;
        case USER_TOP_NAV.teacherProducts._id:
          if (!allowTeacherProductView) {
            return;
          }
          break;
        case USER_TOP_NAV.testBuilder._id:
          if (!showTestBuilderTab) {
            return;
          }
          break;
      }
      return this.renderMenuItem(t(navTranslationKey), VIEW_SELECTION[viewSelectionKey], {
        className, onClick: this[functionKey]
      }, {
        ...(tabNavObj || {})
        // navTranslationKey, navTranslationRolloverKey
      });
    }).filter((tabNavObj) => !!tabNavObj);
  };

  getHelpLink = () => {
    const { contentManager } = this.props;
    if (contentManager.helpWindowUrl !== undefined && contentManager.helpWindowUrl !== null && contentManager.helpWindowUrl !== '') {
      return this.renderMenuItem(
        <Image alt='' aria-label='Help link' role='tab' src={iconHelp} />, null, { className: 'help-nav', onClick: this.showHelpWindow }
      );
    }
  }

  getAdditionalTopNavExternalResources = () => {
    const { t, topNavManager } = this.props;

    const { additionalTeacherTopNavExternalResources } = topNavManager;

    if (!Array.isArray(additionalTeacherTopNavExternalResources) || !additionalTeacherTopNavExternalResources?.length) {
      return null;
    }

    return additionalTeacherTopNavExternalResources.map((tabNavObj) => {
      if (typeof tabNavObj !== 'object' || !tabNavObj?._id) {
        return;
      }

      const className = tabNavObj.className || classNames('resources', tabNavObj._id);
      const navTranslationKey = tabNavObj.navTranslationKey || tabNavObj._id;
      const viewSelectionKey = tabNavObj.viewSelectionKey || 'resources';

      const handleClickExternalResource = () => {
        if (typeof tabNavObj.url === 'string') {
          window.open(tabNavObj.url);
        }
      };

      return this.renderMenuItem(t(navTranslationKey), VIEW_SELECTION[viewSelectionKey], {
        className, onClick: handleClickExternalResource
      }, {
        ...(tabNavObj || {}),
        className,
        navTranslationKey,
        viewSelectionKey
      });
    }).filter((obj) => obj);
  }

  getNotificationLink = () => {
    const { notificationManager, t, topNavManager } = this.props;

    const { topNavNotificationCountLabel, totalNotificationCount } = notificationManager;

    const { allowTopNavEnvelopeNotificationIcon, showFullNotificationBanner } = topNavManager;

    const hasNotifications = totalNotificationCount > 0;
    const allowNotificationsMenuItem = allowTopNavEnvelopeNotificationIcon || hasNotifications;

    if (allowNotificationsMenuItem) {
      let notificationMenuItem;
      if (showFullNotificationBanner) {
        notificationMenuItem = (
          <div className='notification-count-wrapper notification-count'>
            <div>
              {`Notifications (${topNavNotificationCountLabel})`}
            </div>
          </div>
        );
      } else if (topNavManager.allowTopNavEnvelopeNotificationIcon) {
        const { EnvelopeIcon } = this;
        const notificationsLabel = t('notificationsLabel');
        const notificationCountLabel = notificationManager.topNavNotificationCountLabel;
        notificationMenuItem = (
          <EnvelopeIcon
            alt={`${notificationsLabel}: ${notificationCountLabel}`}
            hasNotifications={hasNotifications} />
        );
      } else {
        const notificationsLabel = t('notificationsLabel');
        const notificationCountLabel = notificationManager.topNavNotificationCountLabel;
        notificationMenuItem = (
          <Popup
            hideOnScroll={false}
            hoverable={true}
            on='hover'
            position='bottom center'
            trigger={(
              <div aria-label={`${notificationsLabel}: ${notificationCountLabel}`}
                className='notification-count-wrapper notification-count'>
                <Label circular>
                  {notificationCountLabel}
                </Label>
              </div>
            )}
            wide>
            <div key={1} className='card-value'>{t('notificationsLabel')}</div>
          </Popup>
        );
      }

      return this.renderMenuItem(notificationMenuItem,
        VIEW_SELECTION.NOTIFICATIONS, {
          className: 'help-nav notifications-nav', onClick: this.showNotifications
        }, {
          urlPathname: '/notifications'
        }
      );
    }
  }

  renderProfileMenu = () => {
    const { ProfileMenu, props: { profileMenu, t, userManager } } = this;
    const {
      profile: allowShowProfile = true,
      archivedClasses: allowShowArchivedClasses = true,
      items: profileMenuOptionsFromMainViewOverride,
      logout: allowShowLogout = true
    } = profileMenu || {};

    const { productManager, topNavManager } = this.props;

    const { treatTopNavTabsAsAnchorTags: treatAsHref } = topNavManager;

    const { allowSwitchRole, isSsoUser, isTeacher } = userManager;
    const hasMultipleRoles = UserService.getAvailableRoleCount() > 1;

    const { getTopNavAnchorHref } = TopNavService;

    const {
      PROFILE_MENU,
      profileMenuOrderArray
    } = ProfileMenuService.getProfileMenuInfo() || {};

    let options = [];
    if (UserService.hasActivePermissionId()) {
      const allMenuOptions = profileMenuOrderArray?.map?.((menuItemId) => {
        const menuItemObj = PROFILE_MENU[menuItemId];

        if (!menuItemObj) {
          return null;
        }

        const { _id, className, functionKey, menuTranslationKey, urlPathname, viewSelectionKey } = menuItemObj;

        switch (_id) {
          case PROFILE_MENU.profile?._id:
            if (!allowShowProfile) {
              return null;
            }
            break;
          case PROFILE_MENU.archivedClasses?._id:
            if (!allowShowArchivedClasses || !isTeacher ) {
              return null;
            }
            break;
          case PROFILE_MENU.guidedTour?._id:
            if (!GuidedTourService.shouldAllowGuidedTourLauncher()) {
              return null;
            }
            break;
          case PROFILE_MENU.switchRole?._id:
            if (!allowSwitchRole || !hasMultipleRoles) {
              return null;
            }
            break;
          case PROFILE_MENU.logout?._id:
            if (!allowShowLogout) {
              return null;
            }
            break;
          default:
            break;
        }

        return {
          _id,
          className,
          href: treatAsHref && urlPathname ? getTopNavAnchorHref(urlPathname, VIEW_SELECTION[viewSelectionKey]) : undefined,
          onClick: (event, _data) => {
            if (treatAsHref && urlPathname) {
              event.preventDefault();
            }
            if (functionKey && this[functionKey]) {
              return this[functionKey]();
            }
          },
          text: t(menuTranslationKey)
        };
      }).filter((menuItem) => !!menuItem) || [];

      // If custom items exist, move logout to the end. Otherwise, keep original order.
      if (Array.isArray(profileMenuOptionsFromMainViewOverride) && profileMenuOptionsFromMainViewOverride.length > 0) {
        const profileMenuOptionsWithoutLogout = allMenuOptions.filter((item) => item._id !== 'logout');
        const logoutOption = allMenuOptions.find((item) => item._id === 'logout');

        options = [
          ...profileMenuOptionsWithoutLogout,
          ...profileMenuOptionsFromMainViewOverride,
          ...(logoutOption ? [logoutOption] : [])
        ];
      } else {
        options = allMenuOptions;
      }
    }

    // TODO remove - hardcoded implementation fallback
    // let options = [];
    // if (allowShowLogout) {
    //   options = { onClick: this.handleLogout, text: t('logout') };
    // }
    // if (UserService.hasActivePermissionId()) {
    //   options = [
    //     allowShowProfile && {
    //       className: 'profile',
    //       href: treatAsHref ? getTopNavAnchorHref('/profile', VIEW_SELECTION.PROFILE) : undefined,
    //       onClick: (event, _data) => {
    //         if (treatAsHref) {
    //           event.preventDefault();
    //         }
    //         return this.showProfile();
    //       },
    //       text: t('profile')
    //     },
    //     allowShowArchivedClasses && isTeacher && !isSsoUser && {
    //       className: 'archivedClasses',
    //       href: treatAsHref ? getTopNavAnchorHref('/archived-classes', VIEW_SELECTION.ARCHIVED_CLASSES) : undefined,
    //       onClick: (event, _data) => {
    //         if (treatAsHref) {
    //           event.preventDefault();
    //         }
    //         return this.showArchivedClasses();
    //       },
    //       text: t('archivedClasses')
    //     },
    //     allowSwitchRole && hasMultipleRoles && {
    //       className: 'switchRole',
    //       href: treatAsHref ? getTopNavAnchorHref('/switch-role', VIEW_SELECTION.ROLE_SWITCHER) : undefined,
    //       onClick: (event, _data) => {
    //         if (treatAsHref) {
    //           event.preventDefault();
    //         }
    //         return UserService.showRoleSwitcher(history);
    //       },
    //       text: t('switchRole')
    //     },
    //     ...(profileMenuOptionsFromMainViewOverride || []),
    //     allowShowLogout && { className: 'logout', onClick: this.handleLogout, text: t('logout') }
    //   ];
    // }

    return (!productManager.isFromProduct || productManager.FROM_TEACHER_PRODUCTS_NAV) && (
      <ProfileMenu
        {...profileMenu}
        options={options} />
    );
  }

  render() {
    const {
      additionalClassName = '', history, location, productManager, topNavManager, userManager
    } = this.props;
    const { AriaSkipLink, LogoButtons } = this;

    const { allowAuthKeyTopNavUrlParamAuthentication, treatTopNavTabsAsAnchorTags } = topNavManager;

    let topNavClassName = 'topNav';
    topNavClassName += additionalClassName ? ` ${additionalClassName}` : '';

    const allowClickNavLogo = !productManager.isFromProduct;

    let logoButtonHref;
    if (treatTopNavTabsAsAnchorTags) {
      logoButtonHref = '/';
      if (allowAuthKeyTopNavUrlParamAuthentication) {
        logoButtonHref += `?authKey=${Auth.authKey}`;
        logoButtonHref += `&activePermissionId=${userManager.activePermissionId}`;
      }
    }

    return (
      <Menu
        as={(props) => <div id='top-nav-bar' role='tablist' {...props} ref={this.menuBarRef} />}
        className={topNavClassName}
        fixed='top'
        inverted
        onKeyDown={(e) => TopNavService.topNavHandleKeyDown(e, this.menuBarRef)}>
        <LogoButtons
          history={history}
          href={logoButtonHref}
          location={location}
          navButton={allowClickNavLogo}
          role='tab' />
        <AriaSkipLink mainContentId='main-content' />
        <Menu.Menu
          aria-label='Navigation panel'
          as={(props) => <nav {...props} />}
          position='right'
          role='navigation'>
          <ul className='nav-list' role='tablist'>
            {this.getNavTabs()}
            {this.getHelpLink()}
            {this.getNotificationLink()}
          </ul>
          {this.renderProfileMenu()}
        </Menu.Menu>
      </Menu>
    );
  }
}

SatCoreRegister('TopNav', withRouter(TopNav));
